/**
 * Spec 070 P6: the indicator catalog the live view serves (echo-quant 035, catalog version 1, nine
 * types: the seven of spec 036 plus `levels` and `sweeps`) and the default Manual trade indicator
 * list (in-memory, never persisted). The catalog is client-side because echo-quant publishes none;
 * `opened.catalog_version` is checked against `SUPPORTED_CATALOG_VERSION` and the server's
 * `unsupported_indicator` / `invalid_indicator_params` refusals catch any drift. Param names and
 * ranges are echo-quant's `docs/live-view.md` catalog table.
 */
import type { IndicatorType, ParamValue } from '../types'

const SOURCE = ['open', 'high', 'low', 'close']
const GAP = { name: 'min_gap_ticks', label: 'Min gap (ticks)', type: 'int', default: 0, min: 0, max: 1000 } as const
const SWING = { name: 'swing_length', label: 'Swing length', type: 'int', default: 2, min: 1, max: 20 } as const

export const INDICATOR_CATALOG: IndicatorType[] = [
  {
    type: 'sma',
    label: 'SMA',
    group: 'Built-in',
    kind: 'line',
    description: 'Simple moving average of the chosen price.',
    params: [
      { name: 'period', label: 'Period', type: 'int', default: 20, min: 1, max: 500 },
      { name: 'source', label: 'Source', type: 'choice', default: 'close', options: SOURCE },
    ],
  },
  {
    type: 'ema',
    label: 'EMA',
    group: 'Built-in',
    kind: 'line',
    description: 'Exponential moving average of the chosen price.',
    params: [
      { name: 'period', label: 'Period', type: 'int', default: 20, min: 1, max: 500 },
      { name: 'source', label: 'Source', type: 'choice', default: 'close', options: SOURCE },
    ],
  },
  {
    type: 'bb',
    label: 'Bollinger bands',
    group: 'Built-in',
    kind: 'band',
    description: 'SMA of the close with upper and lower standard-deviation bands.',
    params: [
      { name: 'period', label: 'Period', type: 'int', default: 20, min: 1, max: 500 },
      { name: 'mult', label: 'Std-dev multiplier', type: 'float', default: 2, min: 0.1, max: 10 },
    ],
  },
  {
    type: 'pivots',
    label: 'Daily pivots',
    group: 'Built-in',
    kind: 'levels',
    description: 'Classical pivots from the UTC day that just closed (P, R1, S1, R2, S2).',
    params: [{ name: 'period', label: 'Period', type: 'choice', default: 'day', options: ['day'] }],
  },
  {
    type: 'session',
    label: 'Sessions',
    group: 'Built-in',
    kind: 'shade',
    description: 'Asia, London and New York windows (daylight-saving aware).',
    params: [
      { name: 'asia', label: 'Asia', type: 'bool', default: true },
      { name: 'london', label: 'London', type: 'bool', default: true },
      { name: 'new_york', label: 'New York', type: 'bool', default: true },
    ],
  },
  {
    type: 'fvg',
    label: 'FVG zones',
    group: 'Detections',
    kind: 'zones',
    description: 'Three-candle fair value gaps, active or inverted. Changes only at a timeframe close.',
    params: [GAP],
  },
  {
    type: 'ifvg',
    label: 'Inverted FVG',
    group: 'Detections',
    kind: 'zones',
    description: 'Inverted fair value gaps with a marker per inversion. Changes only at a timeframe close.',
    params: [GAP],
  },
  {
    type: 'levels',
    label: 'Swing levels',
    group: 'Detections',
    kind: 'levels',
    description: 'Swing support and resistance, solid until swept. Changes only at a timeframe close.',
    params: [SWING],
  },
  {
    type: 'sweeps',
    label: 'Sweeps',
    group: 'Detections',
    kind: 'markers',
    description: 'Where a swing level was taken. Changes only at a timeframe close.',
    params: [SWING],
  },
]

/** One entry of the Manual trade indicator list. */
export interface ManualIndicator {
  id: string
  type: string
  color: string
  on: boolean
  params: Record<string, ParamValue>
  name: string
}

/** The default seven (`i1..i7`); names are the auto names (see `indicatorName`). */
export const DEFAULT_INDICATORS: ManualIndicator[] = [
  { id: 'i1', type: 'sma', color: '#E0A030', on: true, params: { period: 20, source: 'close' }, name: 'SMA 20' },
  { id: 'i2', type: 'ema', color: '#6FA8DC', on: false, params: { period: 50, source: 'close' }, name: 'EMA 50' },
  { id: 'i3', type: 'bb', color: '#B38CE0', on: false, params: { period: 20, mult: 2 }, name: 'BB 20 · 2' },
  { id: 'i4', type: 'pivots', color: '#F2EFE6', on: true, params: { period: 'day' }, name: 'Pivots · day' },
  { id: 'i5', type: 'fvg', color: '#E0A030', on: true, params: { min_gap_ticks: 0 }, name: 'FVG ≥ 0 ticks' },
  { id: 'i6', type: 'ifvg', color: '#F2EFE6', on: false, params: { min_gap_ticks: 0 }, name: 'IFVG ≥ 0 ticks' },
  {
    id: 'i7',
    type: 'session',
    color: '#F2EFE6',
    on: false,
    params: { asia: true, london: true, new_york: true },
    name: 'Sessions · Asia/London/NY',
  },
]
